import React, { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react'

import Modal from '../Modal.component'

import { setModalContext } from '../Modal.functions'
import { ModalButton, SecondaryModalButton } from '../Modal.types'
import { ModalContextProps, ModalState, ShowModalProps } from './Modal.context.types'

const ModalContext = createContext<ModalContextProps | undefined>(undefined)

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    title: undefined,
    subtitle: undefined,
    icon: undefined,
    body: undefined,
    primaryButton: undefined,
    secondaryButton: undefined,
    testId: undefined,
    tertiaryButton: undefined
  })

  const [primaryButton, setPrimaryButton] = useState<ModalButton | undefined>(undefined)
  const [secondaryButton, setSecondaryButton] = useState<SecondaryModalButton | undefined>(undefined)
  const [tertiaryButton, setTertiaryButton] = useState<SecondaryModalButton | undefined>(undefined)

  const showModal = (props: ShowModalProps) => {
    setModalState({
      open: true,
      title: props.title,
      subtitle: props.subtitle,
      icon: props.icon,
      body: props.body ?? undefined,
      primaryButton: props.primaryButton,
      secondaryButton: props.secondaryButton,
      testId: props.testId,
      tertiaryButton: props.tertiaryButton
    })
  }

  const handleClose = useCallback(() => {
    setModalState((prev) => ({ ...prev, open: false }))
  }, [])

  // TODO: Debug why this is not working
  const closeModal = useCallback(() => {
    // setModalState((prev) => ({ ...prev, open: false }))
    handleClose()
  }, [handleClose])

  useEffect(() => {
    setModalContext({ show: showModal, isOpen: false, close: closeModal })
  }, [closeModal])

  useEffect(() => {
    setModalContext({ show: showModal, isOpen: modalState.open, close: closeModal })
  }, [modalState.open, closeModal])

  useEffect(() => {
    if (modalState.primaryButton) {
      const { onClick, ...rest } = modalState.primaryButton

      const wrappedOnClick = () => {
        if (onClick) {
          onClick()
        }

        handleClose()
      }

      setPrimaryButton({ ...rest, onClick: wrappedOnClick })
    } else {
      setPrimaryButton(undefined)
    }
  }, [modalState.primaryButton, handleClose])

  useEffect(() => {
    if (modalState.secondaryButton) {
      const { onClick, ...rest } = modalState.secondaryButton

      const wrappedOnClick = () => {
        if (onClick) {
          onClick()
        }

        handleClose()
      }

      setSecondaryButton({ ...rest, onClick: wrappedOnClick })
    } else {
      setSecondaryButton(undefined)
    }
  }, [modalState.secondaryButton, handleClose])

  useEffect(() => {
    if (modalState.tertiaryButton) {
      const { onClick, ...rest } = modalState.tertiaryButton

      const wrappedOnClick = () => {
        if (onClick) {
          onClick()
        }

        handleClose()
      }

      setTertiaryButton({ ...rest, onClick: wrappedOnClick })
    } else {
      setTertiaryButton(undefined)
    }
  }, [modalState.tertiaryButton, handleClose])

  return (
    <ModalContext.Provider value={{ show: showModal, isOpen: modalState.open, close: closeModal }}>
      {children}

      <Modal
        title={modalState.title}
        subtitle={modalState.subtitle}
        icon={modalState.icon}
        body={modalState.body}
        primaryButton={primaryButton}
        secondaryButton={secondaryButton}
        tertiaryButton={tertiaryButton}
        open={modalState.open}
        onClose={handleClose}
        testId={modalState.testId}
      />
    </ModalContext.Provider>
  )
}

export const useModal = (): ModalContextProps => {
  const context = useContext(ModalContext)

  if (!context) {
    throw new Error('useModal must be used within a ModalProvider')
  }

  return context
}
